import React, { useState, useEffect } from 'react';
import { collectionGroup, onSnapshot, collection } from 'firebase/firestore';
import { db } from './firebase';
import {
  Package, Search, CheckCircle, Clock, Truck, MapPin, Mail, BookOpen, Globe,
  User, Calendar, XCircle, ChevronDown, ChevronUp, CreditCard, Eye, Bell, X
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card } from './components/ui/card';
import { Input } from './components/ui/input';

const ORDER_STATUSES = [
  { key: 'Pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { key: 'Processing', label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Package },
  { key: 'Shipped', label: 'Shipped', color: 'bg-purple-100 text-purple-800', icon: Truck },
  { key: 'Completed', label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { key: 'Cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
];

// Normalise legacy statuses
const normalizeStatus = (s) => {
  const map = {
    pending: 'Pending', payment: 'Pending', paid: 'Processing',
    processing: 'Processing', shipped: 'Shipped', delivered: 'Completed',
    cancelled: 'Cancelled',
  };
  return map[s] || s || 'Pending';
};

const PAID_STATUSES = ['Processing', 'Shipped', 'Completed'];

export default function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [authors, setAuthors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [collapsedAuthors, setCollapsedAuthors] = useState({});
  const [collapsedProducts, setCollapsedProducts] = useState({});
  const [waitlistData, setWaitlistData] = useState([]); // all waitlist entries
  const [waitlistPanel, setWaitlistPanel] = useState(null); // { productName, entries[] }

  // Load all waitlist entries across authors
  useEffect(() => {
    const unsub = onSnapshot(collectionGroup(db, 'waitlist'), (snap) => {
      const items = [];
      snap.forEach(d => {
        const pathParts = d.ref.path.split('/');
        if (pathParts.length >= 3 && pathParts[0] === 'authors') {
          items.push({ id: d.id, ...d.data(), _authorId: pathParts[1] });
        }
      });
      setWaitlistData(items);
    }, (err) => console.error('Waitlist collectionGroup error:', err));
    return unsub;
  }, []);

  // Load all authors for display names
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'authors'), (snap) => {
      const map = {};
      snap.forEach(d => {
        const data = d.data();
        map[d.id] = data.penName || data.name || d.id;
      });
      setAuthors(map);
    });
    return unsub;
  }, []);

  // collectionGroup query across all authors' orders
  useEffect(() => {
    const unsub = onSnapshot(collectionGroup(db, 'orders'), (snap) => {
      const items = [];
      snap.forEach(d => {
        // Only include docs from authors/{authorId}/orders path
        const pathParts = d.ref.path.split('/');
        if (pathParts.length >= 3 && pathParts[0] === 'authors') {
          items.push({
            id: d.id,
            ...d.data(),
            _authorId: pathParts[1],
          });
        }
      });
      items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setOrders(items);
      setIsLoading(false);
    }, (err) => {
      console.error('Orders collectionGroup error:', err);
      setIsLoading(false);
    });
    return unsub;
  }, []);

  // Unique author IDs for filter
  const uniqueAuthorIds = [...new Set(orders.map(o => o._authorId || o.authorId))].filter(Boolean);

  const filteredOrders = orders.filter(o => {
    const status = normalizeStatus(o.status);
    const matchStatus = statusFilter === 'all' || status === statusFilter;
    const matchAuthor = authorFilter === 'all' || (o._authorId || o.authorId) === authorFilter;
    const matchSearch = !searchTerm ||
      o.readerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.readerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.bookTitle?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchAuthor && matchSearch;
  });

  const stats = {
    total: orders.length,
    pending: orders.filter(o => normalizeStatus(o.status) === 'Pending').length,
    processing: orders.filter(o => normalizeStatus(o.status) === 'Processing').length,
    shipped: orders.filter(o => normalizeStatus(o.status) === 'Shipped').length,
    completed: orders.filter(o => normalizeStatus(o.status) === 'Completed').length,
    revenue: orders
      .filter(o => PAID_STATUSES.includes(normalizeStatus(o.status)))
      .reduce((s, o) => s + (o.amount || o.total || 0), 0),
  };

  // ── Group orders: Author → Product → orders ──
  const buildAuthorGroups = (orderList) => {
    const authorGroups = {};
    orderList.forEach(order => {
      const authorId = order._authorId || order.authorId || 'unknown';
      const productName = order.productName || 'Unknown Product';

      if (!authorGroups[authorId]) authorGroups[authorId] = {};
      if (!authorGroups[authorId][productName]) authorGroups[authorId][productName] = [];
      authorGroups[authorId][productName].push(order);
    });
    return authorGroups;
  };

  const StatusBadge = ({ status }) => {
    const normalized = normalizeStatus(status);
    const info = ORDER_STATUSES.find(s => s.key === normalized) || ORDER_STATUSES[0];
    const Icon = info.icon;
    return (
      <Badge className={info.color}>
        <Icon className="mr-1 h-3 w-3" />{info.label}
      </Badge>
    );
  };

  const OrderRow = ({ order }) => {
    const isExpanded = expandedOrder === order.id;
    return (
      <div className={`rounded-xl border overflow-hidden shadow-sm ${isExpanded ? 'border-teal-200' : 'border-slate-200'}`}>
        <div
          className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-slate-50"
          onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-xs font-bold text-white shrink-0">
              {order.readerName?.[0]?.toUpperCase() || 'R'}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{order.readerName || order.readerEmail}</p>
              <p className="text-xs text-slate-500">
                {order.bookTitle || 'Unknown Book'} · {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-bold text-teal-600">£{(order.amount || order.total || 0).toFixed(2)}</span>
            <StatusBadge status={order.status} />
            {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
        </div>
        {isExpanded && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4">
            <div className="grid gap-4 md:grid-cols-2 text-sm">
              <div className="space-y-2">
                <p className="font-semibold text-slate-700">Order Details</p>
                <p><span className="text-slate-500">Order ID:</span> {order.id}</p>
                <p><span className="text-slate-500">Product Price:</span> £{(order.productPrice || 0).toFixed(2)}</p>
                {order.shippingCost != null && (
                  <p><span className="text-slate-500">Shipping:</span> £{(order.shippingCost || 0).toFixed(2)}</p>
                )}
                {order.stripeSessionId && (
                  <p><span className="text-slate-500">Stripe:</span> {order.stripeSessionId}</p>
                )}
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-slate-700">Shipping Address</p>
                {order.shippingAddress ? (
                  <div>
                    {order.shippingAddress.line1 && <p>{order.shippingAddress.line1}</p>}
                    {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                    {order.shippingAddress.city && (
                      <p>{order.shippingAddress.city}{order.shippingAddress.postalCode && `, ${order.shippingAddress.postalCode}`}</p>
                    )}
                    {order.shippingAddress.country && <p className="font-semibold">{order.shippingAddress.country}</p>}
                  </div>
                ) : (
                  <p className="text-slate-400">No address on file</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Package className="mx-auto h-12 w-12 animate-pulse text-teal-600" />
        <p className="mt-2 text-slate-600">Loading orders...</p>
      </div>
    );
  }

  const authorGroups = buildAuthorGroups(filteredOrders);

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-start gap-3">
        <Eye className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Platform-wide Order Overview</p>
          <p className="text-xs mt-0.5">
            Read-only view of all orders across all authors. Authors manage their own orders from the Author Dashboard.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-slate-500">Total Orders</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </Card>
        <Card className="rounded-2xl border-yellow-100 bg-yellow-50 p-4 shadow-sm">
          <p className="text-xs text-yellow-700">Pending</p>
          <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
        </Card>
        <Card className="rounded-2xl border-blue-100 bg-blue-50 p-4 shadow-sm">
          <p className="text-xs text-blue-700">Processing</p>
          <p className="text-2xl font-bold text-blue-900">{stats.processing}</p>
        </Card>
        <Card className="rounded-2xl border-purple-100 bg-purple-50 p-4 shadow-sm">
          <p className="text-xs text-purple-700">Shipped / Completed</p>
          <p className="text-2xl font-bold text-purple-900">{stats.shipped + stats.completed}</p>
        </Card>
        <Card className="rounded-2xl border-green-100 bg-green-50 p-4 shadow-sm">
          <p className="text-xs text-green-700">Revenue</p>
          <p className="text-2xl font-bold text-green-900">£{stats.revenue.toFixed(2)}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            type="text" placeholder="Search orders..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-56 py-2 pl-9 pr-4"
          />
        </div>
        <select
          value={authorFilter} onChange={e => setAuthorFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
        >
          <option value="all">All Authors</option>
          {uniqueAuthorIds.map(id => (
            <option key={id} value={id}>{authors[id] || id.slice(0, 8)}</option>
          ))}
        </select>
        <select
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
        >
          <option value="all">All Statuses</option>
          {ORDER_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* Grouped orders: Author → Product → Orders */}
      {filteredOrders.length === 0 ? (
        <Card className="rounded-2xl p-12 text-center">
          <Package className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="text-slate-500">
            {orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(authorGroups).map(([authorId, productGroups]) => {
            const authorName = authors[authorId] || authorId.slice(0, 8);
            const allAuthorOrders = Object.values(productGroups).flat();
            const authorRevenue = allAuthorOrders
              .filter(o => PAID_STATUSES.includes(normalizeStatus(o.status)))
              .reduce((s, o) => s + (o.amount || o.total || 0), 0);
            const authorOrderCount = allAuthorOrders.length;
            const authorShipped = allAuthorOrders.filter(o => ['Shipped', 'Completed'].includes(normalizeStatus(o.status))).length;
            const isAuthorCollapsed = collapsedAuthors[authorId];

            return (
              <div key={authorId} className="rounded-2xl border-2 border-slate-200 overflow-hidden shadow-sm">
                {/* Author header */}
                <Button
                  variant="ghost"
                  onClick={() => setCollapsedAuthors(prev => ({ ...prev, [authorId]: !prev[authorId] }))}
                  className="h-auto w-full rounded-none flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-50 to-slate-100/60 hover:from-slate-100 hover:to-slate-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-sm font-bold text-white shrink-0">
                      {authorName[0]?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-base">{authorName}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {authorOrderCount} order{authorOrderCount !== 1 ? 's' : ''} · {authorShipped} sent · {Object.keys(productGroups).length} product{Object.keys(productGroups).length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-slate-500">Revenue</p>
                      <p className="text-sm font-bold text-teal-700">£{authorRevenue.toFixed(2)}</p>
                    </div>
                    {isAuthorCollapsed ? <ChevronDown className="h-5 w-5 text-teal-600 shrink-0" /> : <ChevronUp className="h-5 w-5 text-teal-600 shrink-0" />}
                  </div>
                </Button>

                {/* Products within this author */}
                {!isAuthorCollapsed && (
                  <div className="divide-y divide-slate-100">
                    {Object.entries(productGroups).map(([productName, productOrders]) => {
                      const productTotal = productOrders.reduce((s, o) => s + (o.amount || o.total || 0), 0);
                      const productRevenue = productOrders
                        .filter(o => PAID_STATUSES.includes(normalizeStatus(o.status)))
                        .reduce((s, o) => s + (o.amount || o.total || 0), 0);
                      const productPlaced = productOrders.length;
                      const productSent = productOrders.filter(o => ['Shipped', 'Completed'].includes(normalizeStatus(o.status))).length;
                      const productKey = `${authorId}__${productName}`;
                      const isProductCollapsed = collapsedProducts[productKey];
                      const productWaitlist = waitlistData.filter(w => w._authorId === authorId && w.productName === productName);

                      return (
                        <div key={productKey}>
                          {/* Product sub-header */}
                          <Button
                            variant="ghost"
                            onClick={() => setCollapsedProducts(prev => ({ ...prev, [productKey]: !prev[productKey] }))}
                            className="h-auto w-full rounded-none flex items-center justify-between px-5 py-3 bg-white hover:bg-slate-50 transition-colors text-left border-l-4 border-teal-300"
                          >
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-slate-500 shrink-0" />
                              <span className="text-sm font-semibold text-slate-800">{productName}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                                {productPlaced} placed
                              </span>
                              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-bold text-green-700">
                                {productSent} sent
                              </span>
                              {productWaitlist.length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setWaitlistPanel({ productName, entries: productWaitlist });
                                  }}
                                  className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs font-bold text-teal-700 hover:bg-teal-100 transition-colors"
                                >
                                  <Bell className="h-3 w-3" />
                                  {productWaitlist.length} waitlist
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-semibold text-teal-600 hidden sm:block">
                                £{productRevenue.toFixed(2)}
                              </span>
                              {isProductCollapsed
                                ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                                : <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />}
                            </div>
                          </Button>

                          {/* Individual orders */}
                          {!isProductCollapsed && (
                            <div className="space-y-2 p-4 bg-white">
                              {productOrders.map(order => <OrderRow key={order.id} order={order} />)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Waitlist Panel */}
      {waitlistPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setWaitlistPanel(null)}>
          <Card className="relative mx-4 w-full max-w-lg rounded-2xl p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Waitlist</h3>
                <p className="text-sm text-slate-500">{waitlistPanel.productName} — {waitlistPanel.entries.length} reader{waitlistPanel.entries.length !== 1 ? 's' : ''} waiting</p>
              </div>
              <button onClick={() => setWaitlistPanel(null)} className="rounded-lg p-1 hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {waitlistPanel.entries
                .sort((a, b) => {
                  const aTime = a.timestamp?.toDate?.() || new Date(0);
                  const bTime = b.timestamp?.toDate?.() || new Date(0);
                  return bTime - aTime;
                })
                .map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{entry.name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{entry.email}</p>
                    </div>
                    <p className="text-xs text-slate-400">
                      {entry.timestamp?.toDate
                        ? entry.timestamp.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'N/A'}
                    </p>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
